import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Action } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { ZoneRegionsPickerActions } from '@states/zone-regions-picker/zone-regions-picker.actions';
import { zoneRegionsPickerFeature } from '@states/zone-regions-picker/zone-regions-picker.feature';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { ZoneRegionsService } from '../../services/zone-regions.service';
import { UtilsService } from '../../edge/utils.service';

export const startLoadingZoneRegionsPicker$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => actions$.pipe(
    ofType(ZoneRegionsPickerActions.startLoadingZoneRegions),
    withLatestFrom(
      store.select(zoneRegionsPickerFeature.selectIsLoading),
      store.select(zoneRegionsPickerFeature.selectIsLoaded),
    ),
    switchMap(([, isLoading, isLoaded]) => {
      if (isLoading || isLoaded) {
        return [];
      }

      return [
        ZoneRegionsPickerActions.setIsLoading({ isLoading: true }),
        ZoneRegionsPickerActions.getZoneRegions(),
        ZoneRegionsPickerActions.loadAllPickerCameras(),
      ];
    }),
  ),
  { functional: true },
);

export const getZoneRegionsPicker$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    utilsService = inject(UtilsService),
  ) => actions$.pipe(
    ofType(ZoneRegionsPickerActions.getZoneRegions),
    switchMap(() => zoneRegionsService.getAllZoneRegionsForPicker().pipe(
      switchMap((zoneRegions) => [
        ZoneRegionsPickerActions.getZoneRegionsSuccess({ zoneRegions: zoneRegions ?? [] }),
        ZoneRegionsPickerActions.setIsLoading({ isLoading: false }),
      ]),
      catchError((response) => [
        ZoneRegionsPickerActions.setIsLoading({ isLoading: false }),
        ZoneRegionsPickerActions.getZoneRegionsFail({ error: utilsService.errMessage(response) }),
      ]),
    )),
  ),
  { functional: true },
);

export const syncZoneRegionsPickerDeleteSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
  ) => actions$.pipe(
    ofType(ZoneRegionsActions.deleteZoneRegionSuccess),
    switchMap(({ id }) => [
      ZoneRegionsPickerActions.removeZoneRegion({ id }),
    ]),
  ),
  { functional: true },
);

export const syncZoneRegionsPickerCreateSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
  ) => actions$.pipe(
    ofType(ZoneRegionsActions.createZoneRegionSuccess),
    switchMap(({ zoneRegion, affectedZoneRegionCameras }) => {
      const actions: Action[] = [ZoneRegionsPickerActions.upsertZoneRegions({ zoneRegions: [zoneRegion] })];
      if (affectedZoneRegionCameras?.length) {
        actions.push(ZoneRegionsPickerActions.upsertPickerCameras({ cameras: affectedZoneRegionCameras }));
      }
      return actions;
    }),
  ),
  { functional: true },
);

/** Apply save response (zone regions + cameras) to picker state so picker stays in sync after bulk save. */
export const syncZoneRegionsPickerSaveSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.saveZoneRegionsWithCamerasSuccess),
      switchMap(({ response }) => {
        const actions: Action[] = [];
        if (response.affectedZoneRegions?.length) {
          actions.push(ZoneRegionsPickerActions.upsertZoneRegions({ zoneRegions: response.affectedZoneRegions }));
        }
        if (response.affectedZoneRegionCameras?.length) {
          actions.push(ZoneRegionsPickerActions.upsertPickerCameras({ cameras: response.affectedZoneRegionCameras }));
        }
        return actions;
      }),
    ),
  { functional: true },
);

export const loadAllPickerCameras$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    utilsService = inject(UtilsService),
  ) => actions$.pipe(
    ofType(ZoneRegionsPickerActions.loadAllPickerCameras),
    switchMap(() => zoneRegionsService.getAllZoneRegionCamerasForPicker().pipe(
      map((cameras) => ZoneRegionsPickerActions.loadAllPickerCamerasSuccess({ cameras: cameras ?? [] })),
      catchError((response) => of(ZoneRegionsPickerActions.loadAllPickerCamerasFail({ error: utilsService.errMessage(response) }))),
    )),
  ),
  { functional: true },
);
